"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeFormScores = exports.FormComputedScoresSchema = exports.findUnansweredRequired = exports.sanitiseAnswersAgainstDefinition = exports.assertDefinitionCompiles = exports.compileSubmissionSchema = exports.evaluateCondition = exports.collectFileAnswerRefs = exports.FileAnswerSchema = exports.FormDefinitionSchema = exports.ScoringConfigSchema = exports.ScoringFormulaSchema = exports.SectionSchema = exports.QuestionSchema = exports.QuestionValidationSchema = exports.safeCompileRegExp = exports.QuestionOptionSchema = exports.InputMaskSchema = exports.AlertSpecSchema = exports.AlertSeveritySchema = exports.ConditionalRuleSchema = exports.ConditionalOperatorSchema = exports.RepeaterColumnSchema = exports.RepeaterColumnTypeSchema = exports.QuestionTypeSchema = void 0;
const zod_1 = require("zod");
// =========================================================================
// FormDefinition schema — a runtime-editable form. One shape drives a shared
// renderer + ingestion endpoint. DD assessment is the first consumer; other
// TTO forms (HOD, Dean, gate forms) follow the same shape.
// =========================================================================
// -------- Question types (UPPERCASE per project convention) --------
exports.QuestionTypeSchema = zod_1.z.enum([
    'TEXT',
    'TEXTAREA',
    'DROPDOWN',
    'YES_NO',
    'MULTI_CHECKBOX',
    'CONFIRMATION',
    'DATE',
    'RATING',
    'RICH_TEXT',
    'FILE',
    /** Dynamic table — user adds rows; each row has the columns declared in
     *  `question.columns`. Answer shape: Array<Record<colKey, cellValue>>. */
    'REPEATER',
    /** Pure editorial block — renders alerts / paragraph only, no input.
     *  Excluded from required checks, scoring, and submission payload. */
    'INFO',
]);
// -------- Repeater column spec --------
exports.RepeaterColumnTypeSchema = zod_1.z.enum(['TEXT', 'EMAIL', 'NUMBER', 'DROPDOWN']);
exports.RepeaterColumnSchema = zod_1.z.object({
    key: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1),
    type: exports.RepeaterColumnTypeSchema,
    required: zod_1.z.boolean().optional(),
    options: zod_1.z.array(zod_1.z.string()).optional(),
});
// -------- Conditional visibility rule --------
exports.ConditionalOperatorSchema = zod_1.z.enum([
    'EQUALS',
    'NOT_EQUALS',
    'ONE_OF',
    'GREATER_THAN',
    'LESS_THAN',
    /** Actual answer is an array that contains `value`. */
    'INCLUDES',
    /** Actual answer is NOT an array, or is an array that does not contain `value`. */
    'NOT_INCLUDES',
]);
const ConditionalValueSchema = zod_1.z.union([
    zod_1.z.string(),
    zod_1.z.number(),
    zod_1.z.boolean(),
    zod_1.z.array(zod_1.z.union([zod_1.z.string(), zod_1.z.number()])),
]);
exports.ConditionalRuleSchema = zod_1.z.lazy(() => zod_1.z.object({
    field: zod_1.z.string().min(1).optional(),
    operator: exports.ConditionalOperatorSchema.optional(),
    value: ConditionalValueSchema.optional(),
    all: zod_1.z.array(exports.ConditionalRuleSchema).optional(),
}));
// -------- Editorial alert block (info / warning / etc. callouts) --------
exports.AlertSeveritySchema = zod_1.z.enum(['INFO', 'WARNING', 'SUCCESS', 'ERROR']);
exports.AlertSpecSchema = zod_1.z.object({
    severity: exports.AlertSeveritySchema,
    /** Main paragraph. Plain text — no markdown rendering. */
    body: zod_1.z.string(),
    /** Optional bulleted lines rendered under the body (e.g. TRL framework). */
    bullets: zod_1.z.array(zod_1.z.string()).optional(),
});
// -------- Known input masks (custom-formatted text inputs) --------
exports.InputMaskSchema = zod_1.z.enum([
    /** SU-DISC-####-####. Auto-prefixed, 8-digit numeric body, dash-formatted. */
    'DISCLOSURE_NUMBER',
]);
// -------- Option (for DROPDOWN / RATING / MULTI_CHECKBOX) --------
exports.QuestionOptionSchema = zod_1.z.object({
    value: zod_1.z.union([zod_1.z.string(), zod_1.z.number()]),
    label: zod_1.z.string(),
    disqualifies: zod_1.z.boolean().optional(),
});
// -------- Regex safety helper --------
/** Compile a regex, or null if the source is bad (instead of throwing). */
const safeCompileRegExp = (source) => {
    try {
        return new RegExp(source);
    }
    catch (_a) {
        return null;
    }
};
exports.safeCompileRegExp = safeCompileRegExp;
// -------- Validation rules attached to a question --------
exports.QuestionValidationSchema = zod_1.z.object({
    minLength: zod_1.z.number().int().nonnegative().optional(),
    maxLength: zod_1.z.number().int().positive().optional(),
    /** Regex applied to TEXT / TEXTAREA / RICH_TEXT. Rejected here if it won't
     *  compile, so a bad regex can never be saved. */
    pattern: zod_1.z
        .string()
        .refine((p) => (0, exports.safeCompileRegExp)(p) !== null, { message: 'Invalid regular expression' })
        .optional(),
    /** Error message shown when `pattern` fails. */
    patternMessage: zod_1.z.string().optional(),
    // File-only
    minFileSizeKb: zod_1.z.number().int().nonnegative().optional(),
    maxFileSizeKb: zod_1.z.number().int().positive().optional(),
    extensions: zod_1.z.array(zod_1.z.string()).optional(),
    multiple: zod_1.z.boolean().optional(),
});
// -------- Question --------
exports.QuestionSchema = zod_1.z.object({
    id: zod_1.z.string().min(1), // canonical camelCase field name
    type: exports.QuestionTypeSchema,
    /** For INFO blocks this can be empty; for input questions it labels the field. */
    label: zod_1.z.string(),
    required: zod_1.z.boolean().default(false),
    // Editorial copy (mirrors JotForm subHeader / subLabel)
    subsection: zod_1.z.string().optional(),
    subsectionDescription: zod_1.z.string().optional(),
    helpText: zod_1.z.string().optional(),
    /** Optional callout blocks rendered above the input (NB warnings, info
     *  panels, TRL reference, etc.). For INFO questions these ARE the body. */
    alerts: zod_1.z.array(exports.AlertSpecSchema).optional(),
    /** Free-form paragraph rendered as the body for INFO blocks. */
    body: zod_1.z.string().optional(),
    /** Optional input mask — renderer plugs in a specialised input for this
     *  field. Only applies to TEXT today. */
    inputMask: exports.InputMaskSchema.optional(),
    options: zod_1.z.array(exports.QuestionOptionSchema).optional(),
    visibleIf: exports.ConditionalRuleSchema.optional(),
    validation: exports.QuestionValidationSchema.optional(),
    /** Per-question score contribution (for RATING / DROPDOWN that score). */
    contributesToScore: zod_1.z.boolean().default(false),
    /** Designer-only soft-delete flag. Marked questions are hidden from the
     *  rendered form + preview and stripped when a version is published, but
     *  remain recoverable in the editor draft until then. */
    softDeleted: zod_1.z.boolean().optional(),
    // ---- FILE-question fields (ignored for other types) ----
    /** When true (default), the field accepts multiple uploads per question. */
    multiple: zod_1.z.boolean().optional(),
    /** Comma-separated MIME types / extensions (e.g. "application/pdf,image/*"). */
    accept: zod_1.z.string().optional(),
    /** Per-file size cap in megabytes. Server enforces a hard 20MB ceiling. */
    maxSizeMB: zod_1.z.number().int().positive().optional(),
    // ---- REPEATER-question fields (ignored for other types) ----
    columns: zod_1.z.array(exports.RepeaterColumnSchema).optional(),
    minRows: zod_1.z.number().int().nonnegative().optional(),
    maxRows: zod_1.z.number().int().positive().optional(),
    addRowLabel: zod_1.z.string().optional(),
});
// -------- Section --------
exports.SectionSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    title: zod_1.z.string().min(1),
    subHeader: zod_1.z.string().optional(),
    /** Optional 1-based number rendered alongside the title ("1. Market"). */
    headingNumber: zod_1.z.number().int().positive().optional(),
    /** Whether this section produces a score in computedScores. */
    scored: zod_1.z.boolean().default(true),
    /** Key under which the section's score is stored (e.g. "scoreMarket"). */
    scoreKey: zod_1.z.string().optional(),
    /** Section-level callouts rendered at the top, before any question. */
    alerts: zod_1.z.array(exports.AlertSpecSchema).optional(),
    questions: zod_1.z.array(exports.QuestionSchema),
});
// -------- Scoring config --------
exports.ScoringFormulaSchema = zod_1.z.enum(['SUM_PER_SECTION']);
exports.ScoringConfigSchema = zod_1.z.object({
    formula: exports.ScoringFormulaSchema.default('SUM_PER_SECTION'),
    totalKey: zod_1.z.string().default('scoreTotal'),
    excludeFromTotal: zod_1.z.array(zod_1.z.string()).default([]),
});
// -------- FormDefinition (top-level) --------
exports.FormDefinitionSchema = zod_1.z.object({
    slug: zod_1.z.string().min(1),
    version: zod_1.z.number().int().positive(),
    title: zod_1.z.string().min(1),
    description: zod_1.z.string().optional(),
    sections: zod_1.z.array(exports.SectionSchema),
    scoring: exports.ScoringConfigSchema,
});
// A FILE answer references uploaded files by storageObjectVersionId, with
// optional display metadata. The attachment row stays the source for
// download/delete/list; this is only what the submission/draft payload carries.
exports.FileAnswerSchema = zod_1.z.object({
    storageObjectVersionId: zod_1.z.string(),
    filename: zod_1.z.string().optional(),
    sizeBytes: zod_1.z.number().optional(),
    mimeType: zod_1.z.string().optional(),
});
/**
 * FILE-answer refs (storageObjectVersionId + questionId) in an answers object;
 * non-object entries ignored. Single source for "which files does this answer
 * set reference", shared by PDF prep and the submission freeze.
 */
const collectFileAnswerRefs = (def, answers) => {
    const refs = [];
    for (const section of def.sections) {
        for (const q of section.questions) {
            if (q.type !== 'FILE')
                continue;
            const value = answers[q.id];
            if (!Array.isArray(value))
                continue;
            for (const entry of value) {
                const versionId = entry && typeof entry === 'object'
                    ? entry
                        .storageObjectVersionId
                    : undefined;
                if (typeof versionId === 'string') {
                    refs.push({ storageObjectVersionId: versionId, questionId: q.id });
                }
            }
        }
    }
    return refs;
};
exports.collectFileAnswerRefs = collectFileAnswerRefs;
// =========================================================================
// Runtime compiler — turns a definition into a Zod validator for a
// submission's answers. Shared by the React resolver and the NestJS handler.
// =========================================================================
// One dropdown/rating value. `z.nan()` admits the unselected-numeric sentinel.
const scalarValueSchema = zod_1.z.union([zod_1.z.string(), zod_1.z.number(), zod_1.z.nan()]);
/** No real selection: blank text ('') or the NaN sentinel for a numeric field. */
const isBlankSelection = (v) => v === '' || (typeof v === 'number' && !Number.isFinite(v));
/**
 * Zod validator for one question's answer value — format only; required and
 * visibleIf are enforced by the caller's superRefine.
 * @param q  the question
 * @returns  a Zod schema, or null for INFO blocks (which carry no value)
 */
const baseFieldSchemaForType = (q) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    switch (q.type) {
        case 'INFO':
            return null;
        case 'TEXT':
        case 'TEXTAREA':
        case 'RICH_TEXT': {
            let s = zod_1.z.string();
            if (((_a = q.validation) === null || _a === void 0 ? void 0 : _a.minLength) != null)
                s = s.min(q.validation.minLength);
            if (((_b = q.validation) === null || _b === void 0 ? void 0 : _b.maxLength) != null)
                s = s.max(q.validation.maxLength);
            if ((_c = q.validation) === null || _c === void 0 ? void 0 : _c.pattern) {
                // Skip a bad regex instead of throwing, so one bad pattern can't
                // crash the whole form's compile.
                const compiled = (0, exports.safeCompileRegExp)(q.validation.pattern);
                if (compiled) {
                    s = s.regex(compiled, (_d = q.validation.patternMessage) !== null && _d !== void 0 ? _d : 'Invalid format');
                }
            }
            return s;
        }
        case 'DATE':
            // ISO calendar date (YYYY-MM-DD). Empty passes; the required check
            // lives in the superRefine.
            return zod_1.z.string().refine((v) => v === '' || (/^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v))), { message: 'Invalid date (expected YYYY-MM-DD)' });
        case 'YES_NO':
            return zod_1.z.enum(['YES', 'NO']);
        case 'CONFIRMATION':
            return zod_1.z.boolean();
        case 'DROPDOWN':
        case 'RATING': {
            // Answer must be one of the option values; an unselected field passes
            // as unanswered. No options → accept any scalar (keeps a required
            // dropdown answerable). Values may mix string + number.
            const values = ((_e = q.options) !== null && _e !== void 0 ? _e : []).map((o) => o.value);
            if (values.length === 0)
                return scalarValueSchema;
            return scalarValueSchema.refine((v) => isBlankSelection(v) || values.includes(v), {
                message: 'Invalid option',
            });
        }
        case 'MULTI_CHECKBOX': {
            const opts = (_f = q.options) !== null && _f !== void 0 ? _f : [];
            const validValues = opts.map((o) => o.value);
            const item = opts.length === 0
                ? zod_1.z.union([zod_1.z.string(), zod_1.z.number()])
                : zod_1.z
                    .union([zod_1.z.string(), zod_1.z.number()])
                    .refine((v) => validValues.includes(v), { message: 'Invalid option' });
            return zod_1.z.array(item);
        }
        case 'FILE':
            // `multiple: false` is single-file — cap the answer at one. Required
            // is enforced separately in the superRefine, so [] still passes here.
            return q.multiple === false
                ? zod_1.z.array(exports.FileAnswerSchema).max(1)
                : zod_1.z.array(exports.FileAnswerSchema);
        case 'REPEATER': {
            const cols = (_g = q.columns) !== null && _g !== void 0 ? _g : [];
            const rowShape = {};
            for (const c of cols) {
                // Apply `.min(1)` while cell is still a string — `.refine()` returns
                // a ZodEffects with no `.min`, so the required check must come first.
                let cell;
                switch (c.type) {
                    case 'NUMBER':
                        // Blank cell = `null` on the FE; the optional handling below
                        // adds null + undefined. Required stays bare number (rejects both).
                        cell = zod_1.z.number();
                        break;
                    case 'EMAIL': {
                        let s = zod_1.z.string();
                        if (c.required)
                            s = s.min(1, { message: 'Required' });
                        cell = s.email({ message: 'Enter a valid email address, like name@example.com' });
                        break;
                    }
                    case 'DROPDOWN': {
                        const opts = (_h = c.options) !== null && _h !== void 0 ? _h : [];
                        let s = zod_1.z.string();
                        if (c.required)
                            s = s.min(1, { message: 'Required' });
                        // '' passes the option check (blank optional cell ok);
                        // required blanks already caught by `.min(1)`.
                        cell = opts.length > 0
                            ? s.refine((v) => v === '' || opts.includes(v), { message: 'Invalid option' })
                            : s;
                        break;
                    }
                    case 'TEXT':
                    default: {
                        let s = zod_1.z.string();
                        if (c.required)
                            s = s.min(1, { message: 'Required' });
                        cell = s;
                    }
                }
                // Optional NUMBER cells arrive as `null` from the FE (blank numeric
                // input), so they must accept null as well as undefined.
                rowShape[c.key] = c.required
                    ? cell
                    : c.type === 'NUMBER'
                        ? cell.nullish()
                        : cell.optional();
            }
            let arr = zod_1.z.array(zod_1.z.object(rowShape));
            if (q.minRows != null)
                arr = arr.min(q.minRows);
            if (q.maxRows != null)
                arr = arr.max(q.maxRows);
            return arr;
        }
    }
};
/**
 * Evaluates a visibleIf / conditional rule against the current answers.
 * @param rule     a leaf rule (field/operator/value), or a compound `all` (AND)
 * @param answers  current answers by question id
 * @returns  true when the rule passes; empty rule = true; compound = every sub-rule
 */
const evaluateCondition = (rule, answers) => {
    if (rule.all)
        return rule.all.every((sub) => (0, exports.evaluateCondition)(sub, answers));
    if (!rule.field || !rule.operator)
        return true;
    const actual = answers[rule.field];
    switch (rule.operator) {
        case 'EQUALS':
            return actual === rule.value;
        case 'NOT_EQUALS':
            return actual !== rule.value;
        case 'ONE_OF':
            return Array.isArray(rule.value) && rule.value.some((v) => v === actual);
        case 'GREATER_THAN':
            return typeof actual === 'number' && typeof rule.value === 'number' && actual > rule.value;
        case 'LESS_THAN':
            return typeof actual === 'number' && typeof rule.value === 'number' && actual < rule.value;
        case 'INCLUDES':
            return Array.isArray(actual)
                && (typeof rule.value === 'string' || typeof rule.value === 'number')
                && actual.includes(rule.value);
        case 'NOT_INCLUDES':
            if (!Array.isArray(actual))
                return true;
            if (typeof rule.value !== 'string' && typeof rule.value !== 'number')
                return true;
            return !actual.includes(rule.value);
    }
};
exports.evaluateCondition = evaluateCondition;
/** A required, visible question whose answer is still empty → raise "Required".
 *  A non-finite number (NaN) is the unselected-numeric-dropdown sentinel, so it
 *  counts as empty — matching `isUnansweredFor`. */
const requiredValueMissing = (q, record) => {
    if (q.type === 'INFO')
        return false;
    if (!q.required)
        return false;
    const visible = q.visibleIf ? (0, exports.evaluateCondition)(q.visibleIf, record) : true;
    if (!visible)
        return false;
    const v = record[q.id];
    return (v === undefined ||
        v === null ||
        (typeof v === 'string' && v.trim() === '') ||
        (typeof v === 'number' && !Number.isFinite(v)) ||
        (typeof v === 'boolean' && v === false && q.type === 'CONFIRMATION') ||
        (Array.isArray(v) && v.length === 0));
};
/**
 * Compiles a Zod schema validating a submission's answers against the
 * definition — per-field formats plus visibleIf-aware required checks.
 * @param def  parsed form definition
 * @returns  Zod object schema; `.parse()` throws on invalid or missing-required
 */
const compileSubmissionSchema = (def) => {
    const shape = {};
    const allQuestions = def.sections
        .flatMap((s) => s.questions)
        .filter((q) => !q.softDeleted);
    for (const q of allQuestions) {
        const base = baseFieldSchemaForType(q);
        // INFO blocks carry no value, so they get no schema entry.
        if (base) {
            // Always allow undefined at parse time; required/conditional checks
            // happen in the superRefine below so we can reference other answers.
            shape[q.id] = base.optional();
        }
    }
    return zod_1.z.object(shape).superRefine((answers, ctx) => {
        const record = answers;
        for (const q of allQuestions) {
            if (requiredValueMissing(q, record)) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    path: [q.id],
                    message: 'Required',
                });
            }
        }
    });
};
exports.compileSubmissionSchema = compileSubmissionSchema;
/** Throws if the def won't compile. Call at publish so a broken form is caught
 *  on write, not when it bricks readers. */
const assertDefinitionCompiles = (def) => {
    (0, exports.compileSubmissionSchema)(def);
};
exports.assertDefinitionCompiles = assertDefinitionCompiles;
/** The value to keep for one answer entry, or null to drop it. Drops entries
 *  whose question is gone / INFO-only, has no field schema, or fails to parse
 *  against the current definition. */
const keptValue = (q, value) => {
    if (!q || q.type === 'INFO')
        return null;
    const fieldSchema = baseFieldSchemaForType(q);
    if (!fieldSchema)
        return null;
    const result = fieldSchema.safeParse(value);
    if (!result.success)
        return null;
    return { value: result.data };
};
/**
 * Drops answer entries that no longer fit the current definition (question
 * removed, type/options changed, now INFO, or soft-deleted).
 * @param def      current form definition
 * @param answers  previously-saved answers by question id
 * @returns  { valid: entries kept, dropped: keys removed }
 */
const sanitiseAnswersAgainstDefinition = (def, answers) => {
    const valid = {};
    const dropped = [];
    // Exclude soft-deleted questions so their stale answers are dropped, matching
    // compileSubmissionSchema / findUnansweredRequired (which both skip them).
    const allQuestions = def.sections
        .flatMap((s) => s.questions)
        .filter((q) => !q.softDeleted);
    const questionById = new Map(allQuestions.map((q) => [q.id, q]));
    for (const [key, value] of Object.entries(answers)) {
        const kept = keptValue(questionById.get(key), value);
        if (kept) {
            valid[key] = kept.value;
        }
        else {
            dropped.push(key);
        }
    }
    return { valid, dropped };
};
exports.sanitiseAnswersAgainstDefinition = sanitiseAnswersAgainstDefinition;
// =========================================================================
// Required-field discovery — the required + visible + unanswered question ids.
// Same missing-value rule as compileSubmissionSchema's superRefine, but returns
// the list (for the FE Review step) instead of raising issues.
// =========================================================================
/** True when `v` is empty for question `q`: null/undefined, blank string, NaN
 *  sentinel, unticked confirmation, or empty array. */
const isUnansweredFor = (q, v) => {
    if (v === undefined || v === null)
        return true;
    if (typeof v === 'string' && v.trim() === '')
        return true;
    if (q.type === 'CONFIRMATION' && typeof v === 'boolean' && v === false)
        return true;
    if (Array.isArray(v) && v.length === 0)
        return true;
    if (typeof v === 'number' && !Number.isFinite(v))
        return true;
    return false;
};
/** A required, visible question that's still unanswered. */
const isMissingRequired = (q, answers) => {
    if (q.type === 'INFO' || q.softDeleted)
        return false;
    if (!q.required)
        return false;
    const visible = q.visibleIf ? (0, exports.evaluateCondition)(q.visibleIf, answers) : true;
    if (!visible)
        return false;
    return isUnansweredFor(q, answers[q.id]);
};
/**
 * Lists required, visible, still-unanswered question ids.
 * @param def      form definition
 * @param answers  current answers by question id
 * @returns  ids of required questions still needing an answer
 */
const findUnansweredRequired = (def, answers) => {
    const missing = [];
    for (const section of def.sections) {
        for (const q of section.questions) {
            if (isMissingRequired(q, answers))
                missing.push(q.id);
        }
    }
    return missing;
};
exports.findUnansweredRequired = findUnansweredRequired;
// =========================================================================
// Score computation — derives per-section + total score from answers.
// =========================================================================
exports.FormComputedScoresSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.number());
const safeNum = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
/** A question's contribution to its section score; 0 if soft-deleted or it
 *  doesn't contribute. */
const questionScore = (q, answers) => {
    if (q.softDeleted)
        return 0;
    if (!q.contributesToScore)
        return 0;
    return safeNum(answers[q.id]);
};
/**
 * Sums each scored section's contributing answers, then the grand total.
 * @param def      form definition (section scoreKeys + scoring config)
 * @param answers  current answers by question id
 * @returns  { [scoreKey]: sectionScore, ..., [totalKey]: total }
 */
const computeFormScores = (def, answers) => {
    const scores = {};
    for (const section of def.sections) {
        if (section.scored && section.scoreKey) {
            let sectionScore = 0;
            for (const q of section.questions) {
                sectionScore += questionScore(q, answers);
            }
            scores[section.scoreKey] = sectionScore;
        }
    }
    if (def.scoring.formula === 'SUM_PER_SECTION') {
        const excluded = new Set(def.scoring.excludeFromTotal);
        const total = Object.entries(scores).reduce((acc, [key, value]) => (excluded.has(key) ? acc : acc + value), 0);
        scores[def.scoring.totalKey] = total;
    }
    return scores;
};
exports.computeFormScores = computeFormScores;
